import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster as SileoToaster } from 'sileo';
import ErrorBoundary from './components/ErrorBoundary';
import { SkeletonTheme } from 'react-loading-skeleton';
import { FullPageSkeleton } from './components/ui/PageSkeleton';
import ScrollToTop from './components/ui/ScrollToTop';
import DomainRouteGuard from './components/DomainRouteGuard';
import ActivityToaster from './components/activity/ActivityToaster';
import VersionBadge from './components/ui/VersionBadge';
import { SKELETON_THEME } from './styles/skeletonTheme';

const LandingPage = lazy(() => import('./components/pages/LandingPage'));
const EnterpriseLandingPage = lazy(() => import('./components/pages/EnterpriseLandingPage'));
const Login = lazy(() => import('./components/pages/Login'));
const PlatformAdminDashboard = lazy(() => import('./components/platform/PlatformAdminDashboard'));
const AdminDashboard = lazy(() => import('./components/dashboards/AdminDashboard'));
const ClientDashboard = lazy(() => import('./components/dashboards/ClientDashboard'));
const VendorDashboard = lazy(() => import('./components/dashboards/VendorDashboard'));
const ReferralPage = lazy(() => import('./components/pages/ReferralPage'));
const LegalPage = lazy(() => import('./components/pages/LegalPage'));
const ForgotPassword = lazy(() => import('./components/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/pages/ResetPassword'));
const NotFound = lazy(() => import('./components/pages/NotFound'));
const DesignLab =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('./design-lab/DesignLab'))
    : null;

function preloadRoute(loader) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(loader, { timeout: 2000 });
    return;
  }
  loader();
}

function preloadDashboardForUser(user) {
  if (!user) return;
  if (user.is_admin && !user.company_id) {
    preloadRoute(() => import('./components/platform/PlatformAdminDashboard'));
  } else if (user.is_admin) {
    preloadRoute(() => import('./components/dashboards/AdminDashboard'));
  } else if (user.is_vendor) {
    preloadRoute(() => import('./components/dashboards/VendorDashboard'));
  } else {
    preloadRoute(() => import('./components/dashboards/ClientDashboard'));
  }
}

function ProtectedRoute({ children, adminOnly = false, vendorOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullPageSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (vendorOnly && !user.is_vendor) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PublicRoute({ children, allowAuthenticated = false }) {
  const { user } = useAuth();

  if (allowAuthenticated) {
    return children;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let title = 'Enrola';
    if (path === '/') title = t.landing?.metaTitle || 'Enrola — Referral Programs That Scale';
    else if (path.startsWith('/enterprise')) title = t.enterprise?.title || 'Enrola for Business';
    else if (path.startsWith('/login')) title = t.login?.title || 'Sign in';
    else if (path.startsWith('/forgot-password') || path.startsWith('/reset-password')) title = t.forgotPassword?.title || 'Password';
    else if (path.startsWith('/admin')) title = t.admin?.adminDashboard || 'Admin';
    else if (path.startsWith('/vendor')) title = t.vendor?.dashboard || 'Representative';
    else if (path.startsWith('/dashboard')) title = t.dashboard?.title || 'Dashboard';
    else if (path.startsWith('/referral')) title = t.referral?.title || 'Referral';
    else if (path.startsWith('/terms')) title = t.legal?.termsTitle || 'Terms';
    else if (path.startsWith('/privacy')) title = t.legal?.privacyTitle || 'Privacy';
    else if (path === '*' || path.startsWith('/404')) title = t.notFound?.title || 'Not found';
    document.title = `${title} · Enrola`;
  }, [location.pathname, t]);

  useEffect(() => {
    preloadDashboardForUser(user);
  }, [user]);

  return (
    <Suspense fallback={<FullPageSkeleton />}>
      <Routes>
      {DesignLab && (
        <>
          <Route path="/design-lab/:slug" element={<DesignLab />} />
          <Route path="/design-lab/:slug/:variantId" element={<DesignLab />} />
        </>
      )}
      <Route
        path="/"
        element={
          <PublicRoute allowAuthenticated={true}>
            <LandingPage />
          </PublicRoute>
        }
      />

      <Route
        path="/enterprise"
        element={
          <PublicRoute allowAuthenticated={true}>
            <EnterpriseLandingPage />
          </PublicRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route path="/referral/:linkCode" element={<ReferralPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />

      <Route
        path="/admin/*"
        element={
          <ProtectedRoute adminOnly={true}>
            {user?.is_admin && !user?.company_id ? <PlatformAdminDashboard /> : <AdminDashboard />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/*"
        element={
          <ProtectedRoute vendorOnly={true}>
            <VendorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          user?.is_admin ? <Navigate to="/admin" replace /> :
          user?.is_vendor ? <Navigate to="/vendor" replace /> :
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function AppWithTheme() {
  const { t } = useLanguage();

  return (
    <SkeletonTheme
      baseColor={SKELETON_THEME.baseColor}
      highlightColor={SKELETON_THEME.highlightColor}
      borderRadius={SKELETON_THEME.borderRadius}
      duration={SKELETON_THEME.duration}
    >
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <MotionConfig reducedMotion="user">
          <a href="#main-content" className="skip-link">
            {t.common?.skipToContent || 'Skip to content'}
          </a>
          <ScrollToTop />
          <div className="min-h-screen">
            <DomainRouteGuard>
              <AppRoutes />
            </DomainRouteGuard>
            <ActivityToaster />
            <VersionBadge />
            <SileoToaster position="top-center" theme="system" />
          </div>
        </MotionConfig>
      </Router>
    </SkeletonTheme>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <AppWithTheme />
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </LanguageProvider>
  );
}

export default App;
